import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'baseui/snackbar'
import useDevTools from '@/containers/DevToolsNew/shared/useDevTools'
import {
  selectContactsItems,
  selectContactsPaginator,
  selectContactsQuery,
  selectContactsPage,
  selectContactsCountry,
  selectContactsOnlyWithContracts,
  selectContactsIsLoading,
  selectContactsError,
  selectContactsActionLoadingKey,
  selectContactsHasSearched, selectContactsLastActionAtByContractId
} from './contactsSelectors'
import { setQuery, setPage, setCountry, setOnlyWithContracts } from './contactsSlice'
import { contactsActions } from './contactsSagas'
import { buildCountrySelectOptions, flagByCountry } from '@/containers/DevToolsNew/Contacts/utils/contactsIcons'
import { buildContactRowViewModel } from '@/containers/DevToolsNew/Contacts/mappers/buildContactRowViewModel'

const useContacts = () => {
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { t } = useDevTools()
  const items = useSelector(selectContactsItems)
  const paginator = useSelector(selectContactsPaginator)
  const query = useSelector(selectContactsQuery)
  const page = useSelector(selectContactsPage)
  const country = useSelector(selectContactsCountry)
  const onlyWithContracts = useSelector(selectContactsOnlyWithContracts)
  const isLoading = useSelector(selectContactsIsLoading)
  const error = useSelector(selectContactsError)
  const actionLoadingKey = useSelector(selectContactsActionLoadingKey)
  const hasSearched = useSelector(selectContactsHasSearched)
  const lastActionAtByContractId = useSelector(selectContactsLastActionAtByContractId)
  const countrySelectOptions = useMemo(() => buildCountrySelectOptions(t), [t])

  const selectedCountryOption =
    countrySelectOptions.find(o => o.id === country) || countrySelectOptions[0]
  const countrySelectValue = [selectedCountryOption]

  const tableRows = useMemo(() => {
    const rawRows = Array.isArray(items) ? items : []
    const filteredRows = onlyWithContracts
      ? rawRows.filter(row => Boolean(row?.hasContract))
      : rawRows

    return filteredRows.map(row =>
      buildContactRowViewModel({
        row,
        t,
        actionLoadingKey,
        isGlobalLoading: Boolean(isLoading),
        dispatch,
        enqueue,
        flagByCountry,
        lastActionAtByContractId,
      }),
    )
  }, [items, onlyWithContracts, t, actionLoadingKey, isLoading, dispatch, enqueue, lastActionAtByContractId])

  const currentPage = Number(paginator?.current_page ?? page ?? 1) || 1
  const lastPage = Number(paginator?.last_page ?? 1) || 1

  const canGoPrevious = currentPage > 1 && !isLoading
  const canGoNext = currentPage < lastPage && !isLoading

  const footerNumbers = {
    total: Number(paginator?.total ?? tableRows.length) || 0,
    from: Number(paginator?.from ?? (tableRows.length > 0 ? 1 : 0)) || 0,
    to: Number(paginator?.to ?? tableRows.length) || 0,
  }

  const fetch = useCallback(() => {
    const trimmed = String(query || '').trim()
    if (trimmed.length < 3) return
    dispatch({ type: contactsActions.FETCH_CONTACT_CONTRACTS_LIST, enqueue, t })
  }, [dispatch, enqueue, query])

  const goToPageAndFetch = useCallback(
    nextPage => {
      dispatch(setPage(nextPage))

      if (hasSearched) {
        dispatch({ type: contactsActions.FETCH_CONTACT_CONTRACTS_LIST, enqueue, t })
      }
    },
    [dispatch, enqueue, hasSearched],
  )

  const goPreviousPage = useCallback(() => {
    if (!canGoPrevious) return
    goToPageAndFetch(currentPage - 1)
  }, [canGoPrevious, currentPage, goToPageAndFetch])

  const goNextPage = useCallback(() => {
    if (!canGoNext) return
    goToPageAndFetch(currentPage + 1)
  }, [canGoNext, currentPage, goToPageAndFetch])

  const onCountrySelectChange = useCallback(
    value => dispatch(setCountry(value?.[0]?.id ?? 'all')),
    [dispatch],
  )

  const setQueryValue = useCallback(value => dispatch(setQuery(value)), [dispatch])
  const setOnlyWithContractsValue = useCallback(
    value => dispatch(setOnlyWithContracts(value)),
    [dispatch],
  )

  return {
    filters: {
      query,
      onlyWithContracts,
      isLoading,
      onQueryChange: setQueryValue,
      onOnlyWithContractsChange: setOnlyWithContractsValue,
      onSearch: fetch,
    },

    countrySelect: {
      options: countrySelectOptions,
      value: countrySelectValue,
      onChange: onCountrySelectChange,
    },

    table: {
      rows: tableRows,
      footer: footerNumbers,
      pagination: {
        canGoPrevious,
        canGoNext,
        goPreviousPage,
        goNextPage,
        currentPage,
        lastPage,
      },
      isLoading,
      error,
    },
  }
}

export default useContacts
